import { Bell, LogOut, User, UserRound } from "lucide-react";
import { useEffect, useState } from "react";
import { InventorySearch } from "@/components/InventorySearch";
import { SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { logout } from "@/services/authService";
import { getStoredAuthUser } from "@/lib/authUser";
import { useNavigate } from "react-router-dom";

interface AppNavbarProps {
  title?: string;
}

const AppNavbar = ({ title: _title }: AppNavbarProps) => {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const navigate = useNavigate();
  const [user, setUser] = useState(() => getStoredAuthUser());

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-card/50 backdrop-blur-sm">
      <div className="h-14 flex items-center gap-3 px-4 min-w-0">
        <div className="flex items-center shrink-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <SidebarTrigger className="text-muted-foreground hover:text-foreground shrink-0" />
            </TooltipTrigger>
            <TooltipContent side="bottom" align="start">
              {collapsed ? "Expand sidebar" : "Collapse sidebar"}
            </TooltipContent>
          </Tooltip>
        </div>

        <div className="flex-1 min-w-0" aria-hidden />

        <div className="flex items-center gap-2 sm:gap-3 shrink-0 border-l border-border/50 pl-3">
          <div className="hidden md:block">
            <InventorySearch />
          </div>

          <Button variant="ghost" size="icon" className="relative text-muted-foreground hover:text-foreground">
            <Bell className="w-4 h-4" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-primary" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-secondary/50 border border-border/50 hover:bg-secondary/70 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="User menu"
              >
                <div className="w-7 h-7 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center">
                  <User className="w-3.5 h-3.5 text-primary" />
                </div>
                <div className="hidden min-w-0 max-w-[200px] text-left md:block">
                  <p className="truncate text-xs font-medium leading-none text-foreground">
                    {(user?.name ?? "").trim() || "—"}
                  </p>
                  <p className="truncate text-[10px] text-muted-foreground">{(user?.email ?? "").trim() || "—"}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="space-y-0.5">
                <p className="text-sm font-semibold text-foreground leading-none">{user?.name || "—"}</p>
                <p className="text-xs text-muted-foreground font-normal">{user?.email || "—"}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="gap-2"
                onClick={() => {
                  navigate("/profile");
                }}
              >
                <UserRound className="h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="gap-2 text-destructive focus:text-destructive"
                onClick={async () => {
                  try {
                    await logout();
                  } catch {
                    // best-effort: still clear local session
                  } finally {
                    localStorage.removeItem("auth_user");
                    window.dispatchEvent(new Event("auth_user_updated"));
                    navigate("/login", { replace: true });
                  }
                }}
              >
                <LogOut className="h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default AppNavbar;
